import { useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ROUTE } from '@/config';
import { fetchBulkOrderByQuery } from '@/api/orderService';
import { useExportFilesAsync, useFetchExportedFiles } from '@/hooksApi/useFile';
import { ProcessNameEnum, usePollingMechanismStore } from '@/stores/pollingMechanismStore';
import { downloadFile } from '@/utils/helpers/fileHelpers';
import {
  ExportScope,
  exportDelivery,
  exportFileName,
  isExportTooLarge,
} from '@/utils/helpers/orderExport';
import useCustomerIO from '@/hooks/useCustomerIO';

export interface ExportRequest {
  scope: ExportScope;
  count: number;
  query: string;
  /** Scope slug for the file name: the tab's list / all / selected / custom. */
  slug: string;
}

/**
 * BOXY-409 — deliver an export: a direct CSV download up to
 * EXPORT_SYNC_MAX_ORDERS, the async queue (Settings › Files) above it — and
 * when core refuses the direct download because the count grew between the
 * preview and the click (EXPORT_TOO_LARGE_FOR_DIRECT_DOWNLOAD).
 * Resolves true when the export was downloaded or queued.
 */
const useExportDelivery = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackEvent } = useCustomerIO();
  const { startPolling } = usePollingMechanismStore();
  const { refetch: refetchExportedFiles } = useFetchExportedFiles();
  const { mutateAsync: exportAsync } = useExportFilesAsync();
  const [isExporting, setIsExporting] = useState(false);

  const filesLink = (text: string) => (
    <span>
      {text}{' '}
      <button
        type='button'
        className='cursor-pointer border-0 bg-transparent p-0 font-semibold text-sd-primary underline'
        onClick={() => navigate(ROUTE.SETTINGS_FILES)}
      >
        {String(t('export_modal.view_files'))}
      </button>
    </span>
  );

  const deliver = async ({ scope, count, query, slug }: ExportRequest): Promise<boolean> => {
    const props = { scope, count, delivery: exportDelivery(count) };
    trackEvent('export_submitted', props);
    setIsExporting(true);

    try {
      if (props.delivery === 'sync') {
        let tooLarge = false;
        const ok = await downloadFile(
          query,
          fetchBulkOrderByQuery,
          exportFileName(slug),
          (error) => (tooLarge = isExportTooLarge(error)),
        );
        if (ok) {
          message.success(String(t('export_modal.downloaded', { count })));
          trackEvent('export_completed', props);
          return true;
        }
        if (!tooLarge) {
          trackEvent('export_failed', props);
          return false;
        }
        props.delivery = 'async';
      }

      await exportAsync(query);
      message.info(filesLink(String(t('export_modal.preparing'))), 8);
      startPolling(
        ProcessNameEnum.EXPORT_ORDER,
        { fileName: 'fileName', toastTextKey: 'export_is_in_queue' },
        async () => {
          const { data } = await refetchExportedFiles();
          const ready = !!data?.object?.items?.[0]?.file;
          if (ready) {
            message.success(filesLink(String(t('export_modal.ready'))), 8);
            trackEvent('export_completed', props);
          }
          return ready;
        },
      );
      return true;
    } catch {
      trackEvent('export_failed', props);
      return false;
    } finally {
      setIsExporting(false);
    }
  };

  return { deliver, isExporting };
};

export default useExportDelivery;
